// Country detection caching utility
// Caches country information to avoid repeated reverse geocoding

interface CountryInfo {
  code: string; // ISO 2-letter code (e.g., "ke", "ng", "us")
  name: string; // Full country name (e.g., "Kenya", "Nigeria", "United States")
  timestamp: number;
}

const CACHE_KEY = 'qls_user_country';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface CachePayload {
  lat: number;
  lng: number;
  /** When null, we intentionally suppress repeated reverse-geocoding attempts. */
  country: CountryInfo | null;
  timestamp: number;
}

const liberiaApiBaseUrl = import.meta.env.VITE_LIBERIA_API_BASE_URL?.replace(/\/$/, '');

/**
 * Get cached country info or fetch from reverse geocoding
 */
export const getUserCountry = async (
  lat: number,
  lng: number,
  googleMapsApiKey?: string
): Promise<CountryInfo | null> => {
  // Check cache first
  const cached = getCachedCountry(lat, lng);
  if (cached !== undefined) {
    // cached can be null (negative cache) or a CountryInfo object
    console.log('Using cached country:', cached, 'for coordinates:', { lat, lng });
    return cached;
  }
  
  console.log('No valid cache found, fetching country from coordinates:', { lat, lng });

  // Preferred: use server-side reverse geocoding to avoid browser key referrer restrictions.
  try {
    if (liberiaApiBaseUrl) {
      const res = await fetch(`${liberiaApiBaseUrl}/api/v1/geocode/reverse`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude: lat, longitude: lng }),
      });

      const payload = await res.json().catch(() => null);
      const data = payload?.data;

      if (res.ok && data?.countryCode && data?.country) {
        const countryInfo: CountryInfo = {
          code: String(data.countryCode),
          name: String(data.country),
          timestamp: Date.now(),
        };
        cacheCountry(lat, lng, countryInfo);
        console.log('Detected and cached country via backend:', countryInfo);
        return countryInfo;
      }

      // Negative-cache if backend is misconfigured/unavailable to avoid retry spam.
      console.error('Failed to detect country via backend reverse geocode:', payload?.error?.message || res.status);
      cacheCountry(lat, lng, null);
      return null;
    }
  } catch (err) {
    console.error('Backend country detection failed:', err);
    cacheCountry(lat, lng, null);
    return null;
  }

  // Fetch from Google Maps Geocoding API
  try {
    if (!googleMapsApiKey) {
      // Avoid making unauthenticated / misconfigured requests; also avoids console spam.
      cacheCountry(lat, lng, null);
      return null;
    }

    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`;
    const response = await fetch(url, {
      // Ensure we send a referrer on cross-origin requests (some deployments default to stricter policies).
      referrerPolicy: 'origin',
    });
    const data = await response.json();

    if (data.status === 'OK' && data.results?.length > 0) {
      const addressComponents = data.results[0].address_components;
      const countryComponent = addressComponents.find((comp: any) =>
        comp.types.includes('country')
      );

      if (countryComponent) {
        const countryInfo: CountryInfo = {
          code: countryComponent.short_name,
          name: countryComponent.long_name,
          timestamp: Date.now(),
        };

        // Cache it
        cacheCountry(lat, lng, countryInfo);
        console.log('Detected and cached country:', countryInfo);
        return countryInfo;
      }
    }

    // REQUEST_DENIED commonly indicates the browser key is not allowed for Geocoding API.
    // Negative-cache to prevent repeated noisy requests.
    console.error('Failed to detect country from coordinates:', data.status);
    cacheCountry(lat, lng, null);
    return null;
  } catch (error) {
    console.error('Error detecting country:', error);
    cacheCountry(lat, lng, null);
    return null;
  }
};

/**
 * Get country from cache if still valid
 */
/**
 * Returns:
 * - CountryInfo: if cached and valid
 * - null: if negative-cached and still valid
 * - undefined: if cache miss/invalid and caller may attempt lookup
 */
const getCachedCountry = (lat: number, lng: number): CountryInfo | null | undefined => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return undefined;

    const data = JSON.parse(cached) as CachePayload;
    
    // Check if cache is still valid
    if (Date.now() - data.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return undefined;
    }

    // Check if coordinates are roughly the same (within ~50km)
    const latDiff = Math.abs(data.lat - lat);
    const lngDiff = Math.abs(data.lng - lng);
    
    if (latDiff > 0.5 || lngDiff > 0.5) {
      // User moved to different area, invalidate cache
      console.log('Cache invalidated due to location change. Cached:', { lat: data.lat, lng: data.lng }, 'Current:', { lat, lng });
      localStorage.removeItem(CACHE_KEY);
      return undefined;
    }

    return data.country ?? null;
  } catch (error) {
    console.error('Error reading country cache:', error);
    return undefined;
  }
};

/**
 * Cache country info
 */
const cacheCountry = (lat: number, lng: number, country: CountryInfo | null): void => {
  try {
    const data: CachePayload = {
      lat,
      lng,
      country,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error caching country:', error);
  }
};

/**
 * Clear country cache (useful for testing or when user changes location significantly)
 */
export const clearCountryCache = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
    console.log('Country cache cleared');
  } catch (error) {
    console.error('Error clearing country cache:', error);
  }
};
